import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, ExternalLink, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ContactPage = () => {
  const contactInfo = [
    {
      icon: MessageCircle,
      title: 'Support Tickets',
      value: 'Open a ticket in your client area for fastest response',
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Beirut, Lebanon',
    },
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Have questions? We're here to help. Open a support ticket in your client area for the fastest response.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Ticket Support */}
            <div>
              <h2 className="text-3xl font-semibold text-foreground mb-6">Contact Support</h2>
              <Card className="card-premium border-primary/20 bg-primary/5">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <MessageCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Get help through your client area
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        Open a support ticket to get help with your hosting account, billing, or technical questions.
                      </p>
                      <Button size="lg" className="w-full" asChild>
                        <a 
                          href="https://961host.net/order/index.php/login" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Open Support Ticket
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* New Customer CTA */}
              <Card className="card-premium mt-6">
                <CardContent className="p-8">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Not a Customer Yet?
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Check out our hosting plans and get started today!
                  </p>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to="/hosting#pricing">
                      View Hosting Plans
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-semibold text-foreground mb-6">Contact Information</h2>
              <div className="space-y-4">
                {contactInfo.map((info, idx) => {
                  const Icon = info.icon;
                  return (
                    <Card key={idx} className="card-premium">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-1">{info.title}</h3>
                            <p className="text-muted-foreground">{info.value}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Existing Customer */}
              <div className="mt-8 p-6 rounded-xl bg-primary/5 border border-primary/20">
                <h3 className="text-xl font-semibold text-foreground mb-3">Existing Customer?</h3>
                <p className="text-muted-foreground mb-4">
                  Log in to your client area to submit support tickets, manage your hosting, 
                  and access all your account features.
                </p>
                <Button variant="default" asChild>
                  <a 
                    href="https://961host.net/order/index.php/login" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Login to Client Area
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
